import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Phone,
  Shield,
  Clock,
  Users,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { RealtimeKitProvider, useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';
import { BrandingProvider } from '../../contexts/BrandingContext';

interface SimpleAttorneyConsultationProps {
  roomId?: string;
  attorneyName?: string;
  firmName?: string;
  requestId?: string;
  onEndConsultation?: () => void;
}

// Generate token using the working debug endpoint (same as client)
const generateToken = async (roomId: string, participantName: string, isHost: boolean = true, maxRetries: number = 3) => {
  console.log(`[Legal Attorney] Generating token for room ${roomId}, participant ${participantName}, host: ${isHost}`);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use the same working debug endpoint as client
      const endpoint = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token';
      const body = { meetingId: roomId, participantName, isHost };
      
      console.log(`[Legal Attorney] Using debug endpoint: ${endpoint}`, body);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      console.log(`[Legal Attorney] Token response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token generation failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log(`[Legal Attorney] Token generated successfully on attempt ${attempt}`);
      return data;

    } catch (error) {
      console.error(`[Legal Attorney] Attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) {
        throw new Error(`Failed to generate token after ${maxRetries} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
};

// Inner component that uses RealtimeKit hooks
const AttorneyMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  attorneyName: string;
  firmName: string;
  roomId: string;
}> = ({ authToken, onEndCall, attorneyName, firmName, roomId }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [consultationTime, setConsultationTime] = useState(0);

  // Initialize meeting
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[Legal Attorney] Initializing with authToken:', authToken ? 'exists' : 'none');
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[Legal Attorney] Meeting initialized successfully');
        
      } catch (error) {
        console.error('[Legal Attorney] Failed to initialize meeting:', error);
        alert(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease refresh the page and try again.`);
      }
    };

    if (authToken && !isInitialized) {
      init();
    }
  }, [authToken, initMeeting, isInitialized]);

  // Timer for consultation time
  useEffect(() => {
    const interval = setInterval(() => {
      setConsultationTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle end call
  const handleEndCall = useCallback(async () => {
    console.log('[Legal Attorney] End call requested, performing cleanup...');
    
    if (meeting && isInitialized) {
      try {
        await meeting.leave();
        console.log('[Legal Attorney] Successfully left video meeting');
      } catch (error) {
        console.warn('[Legal Attorney] Error leaving meeting:', error);
      }
    }
    
    onEndCall();
  }, [meeting, isInitialized, onEndCall]);

  if (!meeting || !isInitialized) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Legal Consultation</h1>
          <h2 className="text-blue-200 text-xl mb-4">{firmName}</h2>
          <p className="text-gray-300 text-lg">Connecting as attorney...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header Bar */}
      <div className="bg-blue-600 text-white px-6 py-3 flex items-center justify-between border-b border-blue-500">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold flex items-center">
              <Shield size={16} className="mr-2 text-yellow-300" />
              Attorney: {attorneyName}
            </h2>
            <p className="text-sm text-blue-200">{firmName}</p>
          </div>
          <div className="px-3 py-1 rounded-full text-xs flex items-center bg-yellow-600 text-white">
            <Shield size={12} className="mr-1" />
            Attorney-Client Privilege
          </div>
          <div className="px-3 py-1 rounded-full text-xs flex items-center bg-green-600 text-white">
            <Users size={12} className="mr-1" />
            Host
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Timer */}
          <div className="text-right">
            <div className="flex items-center text-lg font-mono">
              <Clock size={16} className="mr-2" />
              {formatTime(consultationTime)}
            </div>
            <div className="text-sm text-blue-200">
              Consultation Time
            </div>
          </div>

          {/* End Call Button */}
          <button
            onClick={handleEndCall}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <Phone size={16} className="mr-2" />
            End Consultation
          </button>
        </div>
      </div>

      {/* Main Video Area with RealtimeKit */}
      <div className="flex-1 relative bg-black">
        <div className="h-full w-full">
          <RtkMeeting 
            meeting={meeting} 
            showSetupScreen={false}
            mode="fill"
          />
        </div>

        {/* Attorney info overlay */}
        <div className="absolute top-4 left-4 z-50 bg-black bg-opacity-70 text-white p-3 rounded-lg">
          <h3 className="font-semibold text-green-400">{attorneyName} (Host)</h3>
          <p className="text-sm text-gray-300">{firmName}</p>
          <p className="text-xs text-gray-400 mt-1">Attorney • Connected</p>
        </div>

        {/* Room info overlay */}
        <div className="absolute top-4 right-4 z-50 bg-black bg-opacity-70 text-white p-3 rounded-lg">
          <p className="text-sm">Room: {roomId.substring(0, 8)}...</p>
          <p className="text-xs text-gray-400">Legal Consultation</p>
        </div>
      </div>
    </div>
  );
};

export const SimpleAttorneyConsultation: React.FC<SimpleAttorneyConsultationProps> = ({
  roomId: initialRoomId,
  attorneyName = 'Attorney',
  firmName = 'Smith & Associates',
  requestId,
  onEndConsultation
}) => {
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const attorneyNameFromUrl = urlParams.get('attorneyName') || attorneyName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';
  const debugToken = urlParams.get('authToken'); // Get token from URL if provided

  const [isInitializing, setIsInitializing] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(debugToken || null);
  const [error, setError] = useState<string | null>(null);

  console.log('[Legal Attorney] Component initialized with:', {
    roomId,
    attorneyName: attorneyNameFromUrl,
    firmName,
    requestId: requestIdFromUrl
  });

  // Initialize attorney token
  useEffect(() => {
    const initializeAttorneyCall = async () => {
      if (!roomId) {
        setError('No room ID provided');
        setIsInitializing(false);
        return;
      }

      try {
        console.log('[Legal Attorney] Starting attorney call initialization for room:', roomId);
        
        // Check if we already have a token (from debug mode or URL)
        if (debugToken) {
          console.log('[Legal Attorney] Using token from URL parameters');
          setIsInitializing(false);
        } else {
          // Generate token from debug endpoint
          console.log('[Legal Attorney] Generating token from debug endpoint');
          const tokenData = await generateToken(roomId, attorneyNameFromUrl, true);
          setAuthToken(tokenData.token);
          console.log('[Legal Attorney] Successfully generated token');
          setIsInitializing(false);
        }

      } catch (error) {
        console.error('[Legal Attorney] Failed to initialize attorney call:', error);
        setError(error instanceof Error ? error.message : 'Failed to join consultation');
        setIsInitializing(false);
      }
    };

    initializeAttorneyCall();
  }, [roomId, attorneyNameFromUrl, debugToken]);

  // Handle end call
  const handleEndCall = async () => {
    try {
      console.log('[Legal Attorney] Ending call cleanup...');
      
      // Additional cleanup if needed
      
    } catch (error) {
      console.error('[Legal Attorney] Error during call cleanup:', error);
    } finally {
      // Call parent onEndConsultation
      if (onEndConsultation) {
        onEndConsultation();
      }
    }
  };

  // Loading state
  if (isInitializing || !authToken) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Legal Consultation</h1>
          <h2 className="text-blue-200 text-xl mb-4">{firmName}</h2>
          <p className="text-gray-300 text-lg">Joining consultation as attorney...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          {error && (
            <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-900 via-gray-900 to-red-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl mb-2">Consultation Error</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Render the RealtimeKit meeting
  return (
    <BrandingProvider templateType="legal" initialBranding={{ companyName: firmName }}>
      <AttorneyMeetingComponent
        authToken={authToken}
        onEndCall={handleEndCall}
        attorneyName={attorneyNameFromUrl}
        firmName={firmName}
        roomId={roomId}
      />
    </BrandingProvider>
  );
};